import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { getColumnsVacationBonus } from "./columns";
import { useMemo, useRef, useState } from "react";
import { AlertDialogComponent, DataTable, Pagination } from "@/components/shared";
import AddVacationBonusDialog from "./AddVacationBonusDialog";
import { PaginatedVacationBonusQuery, PaginatedVacationBonusQueryVariables, PaginatedVacationBonusDocument, VacationBonus, VacationBonusEdge } from "@/graphql/__generated__/graphql";
import useUserStore from "@/store/shield";
import { useRemoveVacationBonusMutationHook } from "@/hooks/mutations";
import { Button } from "@/components/ui/button";
import VacationBonusFilter from "./VacationBonusFilter";

interface PaginationRef {
  triggerHandleFilterSubmit: (_newVariables: Partial<PaginatedVacationBonusQueryVariables>) => void;
}

/**
 * VacationBonusComponent component for displaying the vacation bonus table
 * @returns JSX element
 */
function VacationBonusComponent() {
  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["components", "common"]);
  const { removeVacationBonusMutation } = useRemoveVacationBonusMutationHook();

  const { isConfigAdmin } = useUserStore();

  // Pagination config
  const paginationRef = useRef<PaginationRef | null>(null);
  const paginationName = "paginatedVacationBonus";

  // State management
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveVacationBonusOpen, setIsRemoveVacationBonusOpen] = useState(false);
  const [selectedVacationBonus, setSelectedVacationBonus] = useState<VacationBonus | null>(null);

  const onFilterSubmit = (variables: PaginatedVacationBonusQueryVariables) => {
    // When the filter is submitted in the PageComponent,
    // this will call the handleFilterSubmit inside Pagination.
    paginationRef.current?.triggerHandleFilterSubmit(variables);
  };

  const handleRemoveClick = (vacationBonus: VacationBonus) => {
    setSelectedVacationBonus(vacationBonus);
    setIsRemoveVacationBonusOpen(true);
  };

  const handleRemoveVacationBonus = (vacationBonus: VacationBonus) => {
    removeVacationBonusMutation({ variables: { removeVacationBonusId: vacationBonus.id } }).finally(() => {
      setIsRemoveVacationBonusOpen(false);
    });
  };

  const columns = useMemo(() => getColumnsVacationBonus(intl, t, handleRemoveClick, isConfigAdmin), [intl, t, handleRemoveClick, isConfigAdmin]);

  return (
    <>
      <Pagination<VacationBonusEdge, PaginatedVacationBonusQuery, PaginatedVacationBonusQueryVariables>
        ref={paginationRef} props={{ queryDocument: PaginatedVacationBonusDocument, paginationName }}>
        {(dataSlice, loading, displayAmount) => (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 justify-end">
              <VacationBonusFilter onFilterSubmit={onFilterSubmit} displayAmount={displayAmount as number} />
              {isConfigAdmin && <Button
                onClick={() => setIsAddModalOpen(true)}
              >
                {t("common:buttons.add")}
              </Button>}
            </div>
            <DataTable
              loading={loading}
              displayAmount={displayAmount as number}
              data={dataSlice.map(edge => edge.node) ?? []}
              columns={columns}
              enableColumnPinning
              columnPinning={{
                right: ["actions"]
              }}
              className="max-h-[calc(100vh-20rem)]"
            />
          </div>
        )}
      </Pagination>


      <AddVacationBonusDialog
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
      />

      {/* Remove Vacation Bonus Alert Dialog */}
      <AlertDialogComponent
        open={isRemoveVacationBonusOpen}
        onOpenChange={setIsRemoveVacationBonusOpen}
        actionButtonFn={() => handleRemoveVacationBonus(selectedVacationBonus as VacationBonus)}
        title={t("components:alertdialogs.removetitle")}
        bodyText={t("components:alertdialogs.removevacationbonus")}
      />
    </>
  );
}

export default VacationBonusComponent;
